/** 기존 @gc/common/timezone - FE용 */
import moment from 'moment-timezone';

export const BACKEND_DEFAULT_TIMEZONE = 'UTC';

export function getCurrentTimezone(): string {
  return moment.tz.guess();
}

export function formatInTimezone(
  date: Date | string | number,
  timezone: string,
  format = 'YYYY-MM-DD HH:mm'
): string {
  return moment(date).tz(timezone).format(format);
}

/** 날짜를 "3 days ago (2025-01-15)" 형태로 변환 (원본 Mixins.toHumanizedDate) */
export function toHumanizedDate(
  date: Date | string | number | null | undefined,
  opts: {
    hideDate?: boolean;
    daysToPass?: number;
    timezone?: string;
  } = {}
): string {
  if (!date) return '';
  const m = moment(date);
  if (!m.isValid()) return '';
  const forHuman = m.fromNow();
  const daysPast = moment().diff(m, 'days');
  const { hideDate = false, daysToPass = 1, timezone = getCurrentTimezone() } = opts;
  if (daysPast > daysToPass && !hideDate) {
    return `${forHuman} (${formatInTimezone(m.toDate(), timezone, 'YYYY-MM-DD')})`;
  }
  return forHuman;
}

/** 날짜가 오늘인지 여부 */
export function isToday(dateStr?: string): boolean {
  if (!dateStr) return false;
  const d = new Date(dateStr);
  const now = new Date();
  return d.getFullYear() === now.getFullYear()
    && d.getMonth() === now.getMonth()
    && d.getDate() === now.getDate();
}

/** 날짜 포맷, 오늘이면 ' N' 마크 추가 */
export function formatDateWithNew(iso?: string, format = 'YYYY-MM-DD HH:mm'): string {
  if (!iso) return '-';
  const m = moment(iso);
  if (!m.isValid()) return '-';
  const formatted = m.format(format);
  return m.isSame(moment(), 'day') ? `${formatted} N` : formatted;
}

/** 상대 날짜 (compact): "Today" / "1d ago" / "3d ago" / "2mo ago" / "1y ago" */
export function relativeDate(dateStr?: string): string {
  if (!dateStr) return '';
  const diff = moment().diff(moment(dateStr), 'days');
  if (diff === 0) return 'Today';
  if (diff === 1) return '1d ago';
  if (diff < 30) return `${diff}d ago`;
  const months = Math.floor(diff / 30);
  if (months < 12) return `${months}mo ago`;
  return `${Math.floor(months / 12)}y ago`;
}

/**
 * Freshness tier for a channel-stats "Updated At" timestamp, so the relative-time
 * label (`relativeDate`) can be color-coded at a glance instead of read closely:
 * `recent` (≤3 days) / `stale` (≤14 days) / `old` (>14 days). Boundaries match this
 * codebase's own automatic-update cadences — TikTok's 3-day cycle and Instagram's
 * 7-day cycle (see the "bypasses the normal N-day cycle" instant-update tooltips
 * in panel/user.tsx) — so `old` means "missed even the slowest normal cycle
 * twice over", not an arbitrary round number. Missing timestamps default to
 * `old` — no update history is the least fresh state, not neutral.
 */
export type UpdateFreshnessTier = 'recent' | 'stale' | 'old';
export function updateFreshnessTier(dateStr?: string): UpdateFreshnessTier {
  if (!dateStr) return 'old';
  const diff = moment().diff(moment(dateStr), 'days');
  if (diff <= 3) return 'recent';
  if (diff <= 14) return 'stale';
  return 'old';
}

/** 상대 날짜 (verbose): "today" / "3 days ago" / "2 months ago" / "1 year ago" */
export function timeAgo(dateStr?: string): string {
  if (!dateStr) return '';
  const diff = Date.now() - new Date(dateStr).getTime();
  const days = Math.floor(diff / 86_400_000);
  if (days < 1) return 'today';
  if (days < 30) return `${days} days ago`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months} months ago`;
  return `${Math.floor(months / 12)} years ago`;
}

/**
 * 브라우저 로컬 타임존 기준 "YYYY-MM-DD" 또는 "YYYY-MM-DD HH:mm" 포맷.
 * iso 없음 → fallback. 파싱 실패 → onInvalid==='raw'면 원본 문자열 그대로, 아니면 fallback.
 */
export function formatLocalDateTime(
  iso?: string | null,
  opts?: { withTime?: boolean; fallback?: string; onInvalid?: 'fallback' | 'raw' },
): string {
  const { withTime = true, fallback = '-', onInvalid = 'fallback' } = opts ?? {};
  if (!iso) return fallback;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return onInvalid === 'raw' ? iso : fallback;
  const p = (n: number) => String(n).padStart(2, '0');
  const datePart = `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
  return withTime ? `${datePart} ${p(d.getHours())}:${p(d.getMinutes())}` : datePart;
}

/** UTC 기준 "YYYY-MM-DD HH:mm" 포맷 (toISOString 기반, 로컬 타임존 무관). */
export function formatUtcDateTime(iso?: string, fallback = '-'): string {
  if (!iso) return fallback;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? fallback : d.toISOString().slice(0, 16).replace('T', ' ');
}

/** duration(초)을 "2h 30m" 형태로 포맷. zeroAsFallback=true면 0초도 fallback 처리(원본 formatDuration 호환). */
export function formatDurationSeconds(
  sec: number | null | undefined,
  opts?: { fallback?: string; zeroAsFallback?: boolean },
): string {
  const { fallback = '-', zeroAsFallback = false } = opts ?? {};
  if (sec == null || !Number.isFinite(sec) || sec < 0) return fallback;
  if (zeroAsFallback && sec === 0) return fallback;
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const parts: string[] = [];
  if (h > 0) parts.push(`${h}h`);
  if (m > 0 || parts.length === 0) parts.push(`${m}m`);
  return parts.join(' ');
}
